import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import ThemeLock from '../components/ThemeLock';

/**
 * Onboarding — first-login interest picker.
 *
 * Q37 taxonomy cleanup: broad, non-overlapping categories. "Food",
 * "Foodie", "Brunch" merged into a single "Food & Dining" so users don't
 * second-guess which to pick.
 *
 * Q36.3 contrast lock: ThemeLock forces light mode so the lime selected
 * state and label text stay legible on devices auto-switched to dark.
 */
const SUGGESTED = [
  { key: 'fitness', emoji: '🏃', label: 'Fitness' },
  { key: 'wellness', emoji: '🌱', label: 'Wellness' },
  { key: 'food', emoji: '🍣', label: 'Food & Dining' },
  { key: 'coffee', emoji: '☕', label: 'Coffee' },
  { key: 'sports', emoji: '⚽', label: 'Sports' },
  { key: 'outdoors', emoji: '🏕', label: 'Outdoors' },
  { key: 'travel', emoji: '✈️', label: 'Travel' },
  { key: 'music', emoji: '🎵', label: 'Music' },
  { key: 'art', emoji: '🎨', label: 'Art & Culture' },
  { key: 'photography', emoji: '📷', label: 'Photography' },
  { key: 'tech', emoji: '💻', label: 'Tech' },
  { key: 'startups', emoji: '🚀', label: 'Startups' },
  { key: 'networking', emoji: '🤝', label: 'Networking' },
  { key: 'beach', emoji: '🏖', label: 'Beach' },
  { key: 'cycling', emoji: '🚴', label: 'Cycling' },
  { key: 'yoga', emoji: '🧘', label: 'Yoga' },
  { key: 'hiking', emoji: '🥾', label: 'Hiking' },
  { key: 'padel', emoji: '🎾', label: 'Padel' },
];

const MIN_PICKS = 3;
const MAX_PICKS = 8;

export default function Onboarding() {
  const navigate = useNavigate();
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/users/me/needs-onboarding');
        if (alive && !r.data.needs_onboarding) {
          navigate('/');
          return;
        }
      } catch { /* user not logged in — guard via route */ }
      if (alive) setChecked(true);
    })();
    return () => { alive = false; };
  }, [navigate]);

  const toggle = (k) => {
    setPicked(prev => prev.includes(k)
      ? prev.filter(x => x !== k)
      : prev.length >= MAX_PICKS ? prev : [...prev, k]);
  };

  const submit = async () => {
    if (picked.length < MIN_PICKS) return;
    setSaving(true);
    try {
      await api.put('/users/me/interests', { interests: picked });
      navigate('/');
    } catch { /* keep user on screen if save fails */ }
    setSaving(false);
  };

  if (!checked) {
    return (
      <div className="min-h-[100dvh] bg-[#F7F6F2] flex items-center justify-center" style={{ colorScheme: 'light' }}>
        <ThemeLock />
        <Loader2 className="animate-spin text-[#3F5200]" />
      </div>
    );
  }

  return (
    <div
      className="min-h-[100dvh] bg-[#F7F6F2]"
      data-testid="onboarding-page"
      style={{ colorScheme: 'light' }}
    >
      <ThemeLock />
      <header className="px-5 pt-8 pb-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={18} className="text-[#3F5200]" />
          <span className="text-[10px] uppercase tracking-wider font-extrabold text-[#3F5200]">Welcome to ASAP</span>
        </div>
        <h1 className="text-3xl font-extrabold text-[#111] leading-tight tracking-tight">
          What are you into?
        </h1>
        <p className="text-sm text-[#6B6E73] mt-2 leading-relaxed">
          Pick {MIN_PICKS}–{MAX_PICKS} interests so we can show you plans and people you&rsquo;ll actually like.
        </p>
      </header>

      <main className="px-5 pb-32" data-testid="interest-grid">
        <div className="grid grid-cols-2 gap-2.5">
          {SUGGESTED.map(s => {
            const on = picked.includes(s.key);
            return (
              <button
                key={s.key}
                onClick={() => toggle(s.key)}
                data-testid={`interest-${s.key}`}
                aria-pressed={on}
                className={`relative flex items-center gap-3 p-4 rounded-2xl border-2 transition active:scale-[0.98] ${on
                  ? 'border-[#3F5200] bg-[#B6E600] text-black'
                  : 'border-black/[0.08] bg-white text-[#111] hover:border-black/[0.18]'}`}
              >
                <span className="text-2xl">{s.emoji}</span>
                <span
                  className="text-sm font-extrabold"
                  style={{ opacity: 0.95 }} /* Q36.3 — never drop below 0.9 */
                >
                  {s.label}
                </span>
                {on && (
                  <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#3F5200] flex items-center justify-center">
                    <Check size={12} className="text-white" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </main>

      {/* Floating CTA bar */}
      <div
        data-testid="onboarding-cta"
        className="fixed bottom-0 left-0 right-0 bg-[#F7F6F2]/95 backdrop-blur border-t border-black/[0.05] px-5 py-3 pb-[max(env(safe-area-inset-bottom),12px)]"
      >
        <div className="max-w-md mx-auto flex items-center gap-3">
          <p className="flex-1 text-xs font-bold text-[#6B6E73] tabular-nums">
            {picked.length < MIN_PICKS
              ? `Pick ${MIN_PICKS - picked.length} more`
              : `${picked.length} selected`}
          </p>
          <button
            data-testid="onboarding-skip"
            onClick={() => navigate('/')}
            className="text-xs font-bold text-[#6B6E73] px-3 py-2"
          >
            Skip
          </button>
          <button
            data-testid="onboarding-continue"
            onClick={submit}
            disabled={picked.length < MIN_PICKS || saving}
            className="px-5 py-3 rounded-full bg-[#B6E600] text-black font-semibold text-sm flex items-center gap-2 disabled:opacity-40 active:scale-95 transition shadow-lg"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <>Continue <ArrowRight size={14} strokeWidth={2.6} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
